"use client";

import { createContext, useContext, type ReactNode } from "react";

import type {
  ErrorNote,
  ProblemAttemptPartialScore,
  ProblemHistory,
} from "../types/problem";

/** 서버 액션 공통 응답 형태. 앱 구현은 항상 `success` 와 실패 시 `error` 를 준다. */
interface ActionResult {
  success: boolean;
  error?: string;
}

export type UserAnswer = string | Record<string, string | number>;

export interface SaveProblemHistoryOptions {
  partialScore?: ProblemAttemptPartialScore;
  /** 같은 논리적 제출의 자동 재시도 동안 유지하는 멱등 키. */
  clientToken?: string;
}

/** 풀이 저장 결과. 풀이 수를 집계하는 앱은 마일스톤 판정용 값을 함께 준다. */
export interface SaveProblemHistoryResult extends ActionResult {
  trialActivated?: boolean;
  /** 이번 제출이 현재 회차에서 이 문제의 첫 풀이였는지 */
  isFirstAttempt?: boolean;
  /** 저장 후 현재 회차에서 푼 문제 수 */
  attemptedCount?: number;
}

/**
 * 공용 ProblemAnswer 가 쓰는 풀이 데이터 접근 계약.
 *
 * 공용 패키지가 특정 앱의 서버 액션을 직접 import 하면, 빌드하는 앱과 무관하게
 * 그 앱의 저장소에 읽고 쓰게 된다. 실제로 JBGP 가 JCG Firestore 에 풀이 이력을
 * 저장하고 있었다. 그래서 구현을 앱이 주입한다.
 *
 * 메서드가 로그인/익명 쌍으로 나뉘는 것은 현재 JCG 구현을 그대로 반영한 것이다.
 * 소유자 판정을 어댑터 안으로 넣어 쌍을 합치는 것은 후속 과제로 남긴다.
 */
export interface ProblemDataAdapter {
  // 조회
  getProblemHistoryById(
    problemId: string,
  ): Promise<ActionResult & { history: ProblemHistory | null }>;
  getProblemHistoryBySessionId(
    sessionId: string,
    problemId: string,
  ): Promise<ActionResult & { history: ProblemHistory | null }>;

  // 제출
  saveProblemHistory(
    problemId: string,
    isCorrect: boolean,
    userAnswer: UserAnswer,
    options?: SaveProblemHistoryOptions,
  ): Promise<SaveProblemHistoryResult>;
  saveProblemHistoryAnonymous(
    sessionId: string,
    problemId: string,
    isCorrect: boolean,
    userAnswer: UserAnswer,
    options?: SaveProblemHistoryOptions,
  ): Promise<SaveProblemHistoryResult>;

  // 오답노트 — 레거시 단일 노트
  saveErrorNote(
    problemId: string,
    errorNote: string,
    imageUrls?: string[],
  ): Promise<ActionResult>;
  saveErrorNoteAnonymous(
    sessionId: string,
    problemId: string,
    errorNote: string,
    imageUrls?: string[],
  ): Promise<ActionResult>;

  // 오답노트 — 다중 노트
  createErrorNoteItem(
    problemId: string,
  ): Promise<ActionResult & { note?: ErrorNote }>;
  createErrorNoteItemAnonymous(
    sessionId: string,
    problemId: string,
  ): Promise<ActionResult & { note?: ErrorNote }>;
  saveErrorNoteItem(
    problemId: string,
    noteId: string,
    content: string,
    imageUrls?: string[],
    examSessionId?: string,
  ): Promise<ActionResult>;
  saveErrorNoteItemAnonymous(
    sessionId: string,
    problemId: string,
    noteId: string,
    content: string,
    imageUrls?: string[],
    examSessionId?: string,
  ): Promise<ActionResult>;
  deleteErrorNoteItem(
    problemId: string,
    noteId: string,
    examSessionId?: string,
  ): Promise<ActionResult>;
  deleteErrorNoteItemAnonymous(
    sessionId: string,
    problemId: string,
    noteId: string,
    examSessionId?: string,
  ): Promise<ActionResult>;
  renameErrorNoteItem(
    problemId: string,
    noteId: string,
    title: string,
    examSessionId?: string,
  ): Promise<ActionResult>;
  renameErrorNoteItemAnonymous(
    sessionId: string,
    problemId: string,
    noteId: string,
    title: string,
    examSessionId?: string,
  ): Promise<ActionResult>;
  reorderErrorNotes(
    problemId: string,
    noteIds: string[],
  ): Promise<ActionResult>;
  reorderErrorNotesAnonymous(
    sessionId: string,
    problemId: string,
    noteIds: string[],
  ): Promise<ActionResult>;

  // 문제 저장
  saveProblemAsSaved(problemId: string): Promise<ActionResult>;
  saveProblemAsSavedAnonymous(
    sessionId: string,
    problemId: string,
  ): Promise<ActionResult>;
  unsaveProblemAsSaved(problemId: string): Promise<ActionResult>;
  unsaveProblemAsSavedAnonymous(
    sessionId: string,
    problemId: string,
  ): Promise<ActionResult>;

  // 집계 (전체 완료 마일스톤 판정용). 푼 문제 수는 저장 결과(`SaveProblemHistoryResult`)로 받는다.
  getTotalProblemCount(): Promise<ActionResult & { count: number }>;
}

const ProblemDataAdapterContext = createContext<ProblemDataAdapter | null>(null);

export function ProblemDataAdapterProvider({
  adapter,
  children,
}: {
  adapter: ProblemDataAdapter;
  children: ReactNode;
}) {
  return (
    <ProblemDataAdapterContext.Provider value={adapter}>
      {children}
    </ProblemDataAdapterContext.Provider>
  );
}

export function useProblemDataAdapter(): ProblemDataAdapter {
  const adapter = useContext(ProblemDataAdapterContext);
  if (!adapter) {
    throw new Error(
      "ProblemDataAdapter 가 주입되지 않았습니다. 앱 루트에서 ProblemDataAdapterProvider 로 감싸주세요.",
    );
  }
  return adapter;
}
